import { useEffect, useState } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { scrapApi, num, SCRAP_REASON_LABEL, type ScrapEntry } from '@/api/scrap.api';
import { Modal, Field, inputCls, inr, qtyText, noNegativeKeys, SourceTag } from './scrapUi';

// Review a pending scrap entry: fix its scrap value and yard location and approve it (it goes into the
// scrap yard and the ledger is posted), or reject it with a reason (not scrap after all).

interface Props {
  entry: ScrapEntry | null;
  mode: 'approve' | 'reject';
  onClose: () => void;
  onDone: (entry: ScrapEntry) => void;
}

export default function ReviewScrapModal({ entry, mode, onClose, onDone }: Props) {
  const toast = useToast();
  const [scrapRate, setScrapRate] = useState('');
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entry) return;
    setScrapRate(String(num(entry.scrap_rate)));
    setLocation(entry.scrap_location || 'Scrap Yard');
    setNote('');
    setError('');
  }, [entry]);

  if (!entry) return null;

  const qty = num(entry.qty);
  const rate = Number(scrapRate) || 0;
  const scrapValue = qty * rate;
  const bookValue = num(entry.book_value);
  const notRecovered = entry.source_type === 'PROCESS' ? 0 : bookValue - scrapValue;

  const submit = async () => {
    if (mode === 'reject' && !note.trim()) {
      setError('Write why this is not scrap');
      return;
    }
    if (mode === 'approve' && !(Number(scrapRate) >= 0)) {
      setError('Enter the scrap value per unit (0 or more)');
      return;
    }
    setSaving(true);
    try {
      const res = mode === 'approve'
        ? await scrapApi.approve(entry.id, { scrapRate: Number(scrapRate), scrapLocation: location.trim(), note: note.trim() || undefined })
        : await scrapApi.reject(entry.id, note.trim());
      toast.success(res.message || (mode === 'approve' ? 'Scrap approved' : 'Scrap entry rejected'));
      onDone(res.data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the scrap entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={mode === 'approve' ? `Approve ${entry.scrap_number}` : `Reject ${entry.scrap_number}`}
      subtitle={<span className="flex items-center gap-2"><SourceTag source={entry.source_type} /> {entry.source_number}{entry.stage_number != null ? ` · Stage ${entry.stage_number}${entry.stage_name ? ` (${entry.stage_name})` : ''}` : ''}</span>}
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-white cursor-pointer">Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={saving}
            className={`h-9 px-4 rounded-lg text-white text-sm font-semibold disabled:opacity-60 cursor-pointer flex items-center gap-2 ${mode === 'approve' ? 'bg-[#4f46e5] hover:bg-indigo-700' : 'bg-red-600 hover:bg-red-700'}`}>
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving…</> : mode === 'approve'
              ? <><i className="ri-check-double-line" /> {scrapValue === 0 ? 'Approve & Destroy' : 'Approve to Scrap Yard'}</>
              : <><i className="ri-close-circle-line" /> Reject</>}
          </button>
        </>
      )}
    >
      <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
        <div><p className="text-[#94a3b8]">Item</p><p className="font-semibold text-[#1e293b]">{entry.item_name}</p></div>
        <div><p className="text-[#94a3b8]">Quantity</p><p className="font-semibold text-[#1e293b]">{qtyText(qty)} {entry.unit}</p></div>
        <div><p className="text-[#94a3b8]">Reason</p><p className="text-[#1e293b]">{SCRAP_REASON_LABEL[entry.reason]}</p></div>
        <div>
          <p className="text-[#94a3b8]">Material cost (book value)</p>
          <p className="text-[#1e293b]">{qtyText(qty)} × {inr(entry.book_rate)} = <strong>{inr(bookValue)}</strong></p>
        </div>
        {entry.notes && <div className="col-span-2"><p className="text-[#94a3b8]">Notes</p><p className="text-[#475569] whitespace-pre-line">{entry.notes}</p></div>}
        {entry.deduct_stock && (
          <div className="col-span-2 flex items-start gap-1.5 text-amber-700">
            <i className="ri-information-line mt-px" />
            Approving takes {qtyText(qty)} {entry.unit} out of {entry.warehouse_name || 'the warehouse'}.
          </div>
        )}
      </div>

      {mode === 'approve' ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Scrap Value / Unit (₹)" required hint="What one unit fetches from a scrap buyer">
              <input type="number" min="0" step="any" value={scrapRate} onKeyDown={noNegativeKeys}
                onChange={(e) => { setScrapRate(e.target.value.replace(/-/g, '')); setError(''); }} className={inputCls} />
            </Field>
            <Field label="Scrap Location">
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} className={inputCls} maxLength={80} />
            </Field>
          </div>
          <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2.5 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-[#64748b]">Into scrap stock</span><strong className="text-[#4f46e5]">{inr(scrapValue)}</strong></div>
            {entry.source_type === 'PROCESS' ? (
              <div className="flex justify-between"><span className="text-[#64748b]">Taken out of the order's cost (WIP)</span><span className="text-[#1e293b]">{inr(scrapValue)}</span></div>
            ) : entry.source_type === 'PRODUCTION' ? (
              <div className="flex justify-between"><span className="text-[#64748b]">Recovered from the rejection loss</span><span className="text-[#1e293b]">{inr(scrapValue)}</span></div>
            ) : (
              <div className="flex justify-between">
                <span className="text-[#64748b]">{entry.source_type === 'WRITE_OFF' ? 'Write-off loss' : 'Scrap loss'} (not recovered)</span>
                <span className="text-red-600 font-medium">{inr(notRecovered)}</span>
              </div>
            )}
            {scrapValue === 0 && (
              <p className="flex items-start gap-1 text-amber-700 pt-1">
                <i className="ri-error-warning-line mt-px" /> Zero-value scrap is destroyed straight away — nothing goes into the scrap yard.
              </p>
            )}
          </div>
          <Field label="Note" error={error}>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" className={inputCls} />
          </Field>
        </>
      ) : (
        <Field label="Why is this not scrap?" required error={error} hint="e.g. sent for rework, counted twice, wrong item">
          <textarea value={note} onChange={(e) => { setNote(e.target.value); setError(''); }} rows={3}
            className={`${inputCls} h-auto py-2 resize-none`} />
        </Field>
      )}
    </Modal>
  );
}
